// @flow

import React, { useRef, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import actionCreators from "@modules/profile/actionCreator";
import type { ActiveShiftPatternDay } from "@modules/profile/reducer/my-absences/types";
import type { ShiftPatternDay } from "@modules/employee/reducer/employment-details/types";
import * as selectors from "@modules/profile/selectors";
import { accountClientServiceSettings } from "@modules/account/selectors";
import { isCaptureBankHolidaysEnabled, selectBankHolidays } from "@modules/shared/selectors";
import { userEmployeePositions } from "@modules/account/selectors";
import { Button, ShiftPatternTable, type StepProps } from "@components";
import type { ServiceSettings } from "@modules/service/reducer/service-settings/types";

type PropsFromState = $Exact<{
  draft: Object | null,
  bankHolidays: Array<string>,
  captureBankHolidays: boolean | null,
  serviceSettings: ServiceSettings | null,
  activeShiftPatternDays: ActiveShiftPatternDay[],
  employeePositions: Array<Object>,
}>;

type PropsFromDispatch = $Exact<{
  onUpdateDraft: typeof actionCreators.SetMyAbsenceDraft,
}>;

type Props = $Exact<{
  ...StepProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
  setIsValid: Function,
  isEdit: boolean,
}>;

function MyAbsenceShiftPatterns(props: Props) {
  if (!props.editable) {
    return null;
  }

  // Internal State
  const [shiftPatternDaysDraft, setShiftPatternDaysDraft] = useState(null);
  const isValidData = useRef(false);

  function data() {
    let res = [];

    //add absence
    if (!props.isEdit) {
      res = shiftPatternDaysDraft || [];
    } else {
      // when update the  initial state is empty, and we need to get the time lost list from the absence data(draft)
      // after render method pass, the state will be updated and in next render
      // the state will be used for showing the data as we do in reporting new absence
      res = !shiftPatternDaysDraft ? props.draft?.daysLost || [] : shiftPatternDaysDraft || [];
    }

    return res;
  }

  function activePositionRoles() {
    return (props.employeePositions || [])
      .filter(i => i?.isActive === true)
      .map(ep => ({
        value: ep.id,
        label: ep.jobTitle,
      }));
  }

  function shiftPatternDaysConfig(): ShiftPatternDay[] {
    return props.activeShiftPatternDays.map(el => ({
      day: el.weekDay,
      isWorkingDay: el.isWorkingDay,
      shiftDurationSec: el.shiftDurationSec,
    }));
  }

  function setIsValidTimeLossData(valid: boolean): void {
    isValidData.current = valid;
  }

  function onChangeTimeLossData(_shiftPatternDaysDraft: []): void {
    setShiftPatternDaysDraft(_shiftPatternDaysDraft);
  }

  function onSubmitTimeLossData(): void {
    const updatedDraft = { ...props.draft, daysLost: data() };

    // call parent function
    props.setIsValid(isValidData.current);

    props.onUpdateDraft(updatedDraft);

    // submit the step - load next step
    if (props.handleSubmit) {
      props.handleSubmit(updatedDraft, true);
    }
  }

  return (
    <>
      <div className="w-100 d-flex justify-content-center flex-column">
        <ShiftPatternTable
          absence={props.draft}
          employeePositions={activePositionRoles()}
          onValidate={setIsValidTimeLossData}
          onChange={onChangeTimeLossData}
          timeLostShiftPattern={data()}
          shiftPatternDaysConfig={shiftPatternDaysConfig()}
          bankHolidays={props.bankHolidays}
          captureBankHolidays={props.captureBankHolidays}
          minimumAbsenceDuration={props.serviceSettings?.minimumAbsenceDuration}
          showRepeatButton
        />
      </div>
      <div className="w-100 d-flex justify-content-end my-3">
        <Button
          materialIcon="fast_forward"
          onClick={onSubmitTimeLossData}
          disabled={!isValidData.current}
        >
          {props.t("NEXT")}
        </Button>
      </div>
    </>
  );
}

const mapStateToProps = (state): PropsFromState => ({
  draft: selectors.myAbsenceDraft(state),
  activeShiftPatternDays: selectors.myAbsenceActiveShiftPatternDays(state),
  bankHolidays: selectBankHolidays(state),
  captureBankHolidays: isCaptureBankHolidaysEnabled(state),
  serviceSettings: accountClientServiceSettings(state),
  employeePositions: userEmployeePositions(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  onUpdateDraft: actionCreators.SetMyAbsenceDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(MyAbsenceShiftPatterns);
